import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { WelcomeStep } from "./WelcomeStep/WelcomeStep";
import { getCurrentCulture, localize } from "client_legacy/ndaw/culture";
import {
	BusyIndicator,
	DatePicker,
	Form,
	FormattedText,
	FormItem,
	InputWithMoreInfo,
	Select,
	TextArea,
	TextInput,
	useAlertDialog,
	ValidationError
} from "client/Components";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import {
	doesNotExceedLength,
	hasMaximumEntries,
	hasMaxSize,
	isNotEmpty,
	isNotNullNorUndefined,
	isSupportedFileType,
	isValidDate
} from "client/Validation/rules";
import { MAX_TAG_COUNT, UserTagSelect } from "../Settings/UserProfile/UserTagSelect";
import { useLoadUserProfileViewModel } from "../Settings/UserProfile/useLoadUserProfileViewModel";
import {
	getUserProfileImageUrl,
	SaveUserPersonalInfoModel,
	saveUserPersonalInfoStep,
	UserIdentityModel,
	UserProfileEditRestrictions,
	UserProfileModel,
	UserPronounKind
} from "client/Api/UserProfile/service";
import { TagKind } from "client/Api/Tags/tags";
import image from "Content/Images/personal-info-step.svg";
import { getUtcTimeZone, TimeZone } from "client/DateAndTime/timezones";
import {
	EMPTY_ARRAY,
	isNotNil,
	getDataUrl
} from "client/Misc/utils";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { convertToDateOnly } from "client/DateAndTime/dateUtils";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import { AvatarSelector } from "../PersonalInformationPage/AvatarSelector";
import { useUserProfileStore } from "../Settings/UserProfile/userProfileStore";
import { IValueInputAttachable } from "client/Forms/attachable";
import styles from "./PersonalInfoStep.module.less";
import { ContentLayout } from "./ContentLayout";
import { pronounsOptions } from "../Settings/UserProfile/UserProfileForm";
import { Base64FileUriUploadWithMetadataModel, fileMetadataSymbol } from "client/Misc/commonTypes";

export type PersonalInfoStepProps = {
	onNext(): void;
};

export const PersonalInfoStep = (props: PersonalInfoStepProps): ReactElement => {
	const {
		onNext
	} = props;

	const [formData, setFormData] = useState<FormData>(EMPTY_FORM_DATA);
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [currentAvatarId, setCurrentAvatarId] = useState<string | null>(null);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const setCurrentUserIdentity: (patch: Partial<Omit<UserIdentityModel, "userId">>) => void = useUserProfileStore(x => x.setCurrentUserIdentity);
	const [alertDialog, openAlertDialog] = useAlertDialog();

	const {
		viewModel,
		status
	} = useLoadUserProfileViewModel();

	const validationConfiguration = useMemo(
		(): IValidationConfiguration<FormData> => {
			const result: IValidationConfiguration<FormData> = staticValidationConfiguration.clone();

			if (!viewModel) {
				return result;
			}

			const {
				avatarSelectorOptions
			} = viewModel;

			result.forScalar("avatar")
				.add(
					hasMaxSize(
						avatarSelectorOptions.maxFileSize
					),
					isSupportedFileType(
						avatarSelectorOptions.supportedImageTypes
					)
				);

			return result;
		},
		[viewModel]
	);

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const editRestrictions: UserProfileEditRestrictions | undefined = viewModel?.editRestrictions;
	const emailAddress: string | nil = viewModel?.model
		.emailAddress;

	useEffect(
		(): void => {
			if (!viewModel) {
				return;
			}

			const {
				model
			} = viewModel;

			const saveModel: FormData = convertToSaveModel(model);

			setFormData(saveModel);
			setCurrentAvatarId(model.avatarId);
		},
		[viewModel]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (status !== "ready" || isBusy) {
				return;
			}

			setIsBusy(true);

			try {
				const validationResult: InputValidationResult = await formHandler.validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				const {
					newAvatarId
				} = await saveUserPersonalInfoStep(
					prepareForSaving(formData),
					globalCancellation
				);

				const {
					firstName,
					lastName
				} = formData;

				setCurrentAvatarId(newAvatarId);
				setCurrentUserIdentity({
					firstName,
					lastName,
					avatarId: newAvatarId
				});

				setIsBusy(false);
				onNext();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!formHandler.validation.tryProcessServerError(e)) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
				setIsBusy(false);
			}
		},
		[
			alertManager,
			formHandler.validation,
			globalCancellation,
			isBusy,
			status,
			onNext,
			formData,
			setCurrentUserIdentity
		]
	);

	const birthdayMoreInfoHandler = useCallback(
		(): void => {
			openAlertDialog({
				title: localize("_BirthYear"),
				children: localize("_YourBirthYearWontBeDisplayedInYourProfileOrInTheBirthdaysTileOnYourWorkspace"),
				showCloseButton: true,
				trackingId: "birth-year-info-dialog"
			});
		},
		[openAlertDialog]
	);

	return (
		<>
			<WelcomeStep.Left
				trackingId="personal-information-step"
			>
				{isBusy || status === "loading" && (
					<BusyIndicator />
				)}
				<ContentLayout
					title={localize("_UserInfoStepTitle")}
					explanation={
						formHandler.value
							? (
								<FormattedText
									text={localize("_UserInfoStepExplanation")}
									thingsToReplace={[]}
								/>
							)
							: undefined
					}
				>
					<Form onSubmit={saveHandler}>
						<ProfilePicture
							attachable={formHandler.for("avatar")}
							canEditAvatar={viewModel
								? !viewModel
									.editRestrictions
									.profileImage
									.isDisabled
								: false
							}
							currentAvatarId={currentAvatarId}
							avatarContentTypes={viewModel
								? viewModel
									.avatarSelectorOptions
									.supportedImageTypes
								: EMPTY_ARRAY
							}
						/>
						<FormItem
							label={localize("_FirstName")}
							isRequired
						>
							<TextInput
								{...formHandler.for("firstName")
									.attachToTextInput()}
								isDisabled={editRestrictions?.firstName.isDisabled}
							/>
							<ValidationError
								{...formHandler.for("firstName")
									.attachToValidationError()}
							/>
						</FormItem>

						<FormItem
							label={localize("_LastName")}
							isRequired
						>
							<TextInput
								{...formHandler.for("lastName")
									.attachToTextInput()}
								isDisabled={editRestrictions?.lastName.isDisabled}
							/>
							<ValidationError
								{...formHandler.for("lastName")
									.attachToValidationError()}
							/>
						</FormItem>

						<FormItem label={localize("_Pronouns")}>
							<Select
								{...formHandler.for("pronouns").attachToSelect()}
								options={pronounsOptions}
								menuTitle={localize("_Pronouns")}
							/>

							<ValidationError
								{...formHandler.for("pronouns").attachToValidationError()}
							/>
						</FormItem>

						<FormItem
							label={localize("_Email")}
						>
							<TextInput
								value={emailAddress ?? ""}
								type="email"
								isDisabled
							/>
						</FormItem>
						<FormItem
							label={localize("_PhoneNumber")}
						>
							<TextInput
								{...formHandler.for("phoneNumber")
									.attachToTextInput()}
								type="tel"
								isDisabled={editRestrictions?.phone.isDisabled}
							/>
							<ValidationError
								{...formHandler.for("phoneNumber")
									.attachToValidationError()}
							/>
						</FormItem>
						<FormItem label={localize("_Birthday")}>
							<InputWithMoreInfo
								onMoreInfo={birthdayMoreInfoHandler}
								trackingId="birthday-more-info"
							>
								<DatePicker
									{...formHandler.for("birthday")
										.attachToDatePicker()
									}
									mode="date"
									timezone={birthdayTimezone}
								/>
							</InputWithMoreInfo>
							<ValidationError
								{...formHandler
									.for("birthday")
									.attachToValidationError()
								}
							/>
						</FormItem>
						<FormItem
							label={localize("_AboutMe")}
						>
							<TextArea
								{...formHandler.for("aboutMe")
									.attachToTextArea()
								}
								rows={6}
							/>
							<ValidationError
								{...formHandler.for("aboutMe")
									.attachToValidationError()
								}
							/>
						</FormItem>
						<UserTagSelect
							label={localize("_SchoolsAndEducation")}
							attachable={formHandler.for("education")}
							kind={TagKind.UserEducation}
							placeholder={localize("_AddATag")}
						/>
						<UserTagSelect
							label={localize("_InterestsAndHobbies")}
							attachable={formHandler.for("hobbies")}
							kind={TagKind.UserHobbies}
							placeholder={localize("_AddATag")}
						/>
					</Form>

					<ContentLayout.Buttons
						onContinue={saveHandler}
						onSkip={onNext}
					/>
				</ContentLayout>
			</WelcomeStep.Left>
			<WelcomeStep.Right
				imageUrl={image}
				text={localize("_AllProvidedContactOptionsWillBeAvailableInTheAddressBook")}
			/>
			{alertDialog}
		</>
	);
};
PersonalInfoStep.displayName = "PersonalInfoStep";

const birthdayTimezone: TimeZone = getUtcTimeZone(getCurrentCulture());

type FormData = OverrideProperties<
	SaveUserPersonalInfoModel,
	{
		avatar: OptionalModel<Base64FileUriUploadWithMetadataModel>;
	}
>;

const EMPTY_FORM_DATA: FormData = {
	avatar: { isSet: false },
	firstName: null,
	lastName: null,
	phoneNumber: null,
	birthday: null,
	aboutMe: null,
	education: [],
	hobbies: [],
	pronouns: UserPronounKind.None
};

const staticValidationConfiguration: IValidationConfiguration<FormData> =
	createValidationConfiguration<FormData>();

staticValidationConfiguration.forScalar("firstName")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

staticValidationConfiguration.forScalar("lastName")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

staticValidationConfiguration.forScalar("phoneNumber")
	.add(
		doesNotExceedLength(255)
	);

staticValidationConfiguration.forScalar("birthday")
	.add(
		isValidDate()
	);

staticValidationConfiguration.forScalar("aboutMe")
	.add(
		doesNotExceedLength(400)
	);

staticValidationConfiguration.forCollection("education")
	.add(
		isNotNullNorUndefined(),
		hasMaximumEntries(MAX_TAG_COUNT)
	);

staticValidationConfiguration.forCollection("hobbies")
	.add(
		isNotNullNorUndefined(),
		hasMaximumEntries(MAX_TAG_COUNT)
	);

function convertToSaveModel(
	model: UserProfileModel
): FormData {
	return {
		avatar: { isSet: false },
		firstName: model.firstName,
		lastName: model.lastName,
		phoneNumber: model.phoneNumber,
		birthday: parseDtoDate(model.birthday),
		aboutMe: model.aboutMe,
		education: model.education,
		hobbies: model.hobbies,
		pronouns: model.pronouns
	};
}

function prepareForSaving(
	model: SaveUserPersonalInfoModel
): SaveUserPersonalInfoModel {
	const result: SaveUserPersonalInfoModel = { ...model };

	if (isNotNil(result.birthday)) {
		const { birthday } = result;

		const birthDayDate: DateOnly = convertToDateOnly(
			birthday,
			birthdayTimezone
		);

		result.birthday = new Date(
			Date.UTC(
				birthDayDate.year,
				birthDayDate.monthIndex,
				birthDayDate.day
			)
		);
	}

	return result;
}

type ProfilePictureProps = {
	attachable: IValueInputAttachable<OptionalModel<Base64FileUriUploadWithMetadataModel>>;
	canEditAvatar: boolean;
	currentAvatarId: string | null;
	avatarContentTypes?: readonly string[];
};

const ProfilePicture = (props: ProfilePictureProps): ReactElement => {
	const {
		attachable,
		currentAvatarId,
		avatarContentTypes,
		canEditAvatar
	} = props;

	const avatarUrl = useMemo(
		(): string | undefined => {
			const selectedAvatar: OptionalModel<Base64FileUriUploadWithMetadataModel> = attachable.value;

			if (selectedAvatar.isSet) {
				return selectedAvatar.value
					.uri;
			}

			if (isNotNil(currentAvatarId)) {
				return getUserProfileImageUrl(currentAvatarId);
			}

			return undefined;
		},
		[attachable.value, currentAvatarId]
	);

	const avatarChangeHandler = useCallback(
		async (
			file: File
		): Promise<void> => {
			const fileUrl: string = await getDataUrl(file);

			const {
				reducer,
				validation
			} = attachable;

			reducer.setValue({
				isSet: true,
				value: {
					uri: fileUrl,
					filename: file.name,
					[fileMetadataSymbol]: {
						size: file.size,
						mimeType: file.type
					}
				}
			});

			validation.triggerInputFinished();
		},
		[attachable]
	);

	return (
		<FormItem
			label={localize("_ProfilePicture")}
		>
			<div className={styles.pictureContainer}>
				<AvatarSelector
					{...(canEditAvatar
						? {
							isReadonly: false,
							onChange: avatarChangeHandler,
							supportedContentTypes: avatarContentTypes
						}
						: {
							isReadonly: true
						}
					)}
					avatarUrl={avatarUrl}
					placeholder={{ kind: "user-icon" }}
					trackingId="avatar-selector"
				/>
			</div>
			<ValidationError
				{...attachable.attachToValidationError()}
			/>
		</FormItem>
	);
};
ProfilePicture.displayName = "ProfilePicture";